import { Effect, Result, Schema, SchemaGetter, SchemaIssue } from "effect";

import { developmentIdentifier, ApprovalId, CandidateId, ChangeId, CheckRunId, EvidenceId, FindingId, PrincipalId, ReviewRunId, RunId, WorkspaceId } from "./development-identifiers.ts";
import { stationPath, StationPath } from "./station-path.ts";

export const REVIEW_DEEP_LINK_VERSION = 1 as const;

export const REVIEW_SECTIONS = [
  "summary",
  "changes",
  "conflicts",
  "findings",
  "checks",
  "reviews",
  "activity"
] as const;
export const ReviewSection = /* @__PURE__ */ (() => Schema.Literals(REVIEW_SECTIONS))();
export type ReviewSection = typeof ReviewSection.Type;

export const REVIEW_COMPARISON_MODES = [
  "full-change",
  "since-checkpoint",
  "merge-impact"
] as const;
export const ReviewComparisonMode = /* @__PURE__ */ (() => Schema.Literals(REVIEW_COMPARISON_MODES))();
export type ReviewComparisonMode = typeof ReviewComparisonMode.Type;

export const REVIEW_DEEP_LINK_ACTIONS = [
  "resolve-conflict",
  "run-check",
  "complete-review",
  "disposition-finding",
  "request-approval",
  "refresh-target",
  "refresh-authority"
] as const;
export const ReviewDeepLinkAction = /* @__PURE__ */ (() => Schema.Literals(REVIEW_DEEP_LINK_ACTIONS))();
export type ReviewDeepLinkAction = typeof ReviewDeepLinkAction.Type;

const domainError = Symbol("ReviewDeepLink/domain-error");
// Reuse domain decoders and retain their tagged diagnostics in the synchronous
// compatibility facade. Schema callers still receive structured Schema issues.
const domainField = <S extends Schema.Constraint>(schema: S, decode: (input: unknown) => S["Type"]) =>
  Schema.Unknown.pipe(Schema.decodeTo(schema, {
    decode: SchemaGetter.transformOrFail(input => Effect.try({
      try: () => decode(input),
      catch: error => new SchemaIssue.InvalidValue({
        message: error instanceof Error ? error.message : "Review deep-link field is invalid.",
        [domainError]: error
      })
    })),
    encode: SchemaGetter.transform((input: unknown) => input)
  }));
const domainFailure = (issue: SchemaIssue.Issue): unknown => {
  if (issue._tag === "InvalidValue") return issue.annotations === undefined ? undefined :
    Object.getOwnPropertyDescriptor(issue.annotations, domainError)?.value;
  if ("issue" in issue) return domainFailure(issue.issue);
  if ("issues" in issue) return issue.issues.map(domainFailure).find(error => error !== undefined);
  return undefined;
};

const ReviewDeepLinkValue = /* @__PURE__ */ (() => Schema.Struct({
  version: Schema.Literal(REVIEW_DEEP_LINK_VERSION),
  changeId: domainField(ChangeId, value => developmentIdentifier("change", value)).pipe(Schema.optionalKey),
  candidateId: domainField(CandidateId, value => developmentIdentifier("candidate", value)).pipe(Schema.optionalKey),
  workspaceId: domainField(WorkspaceId, value => developmentIdentifier("workspace", value)).pipe(Schema.optionalKey),
  section: ReviewSection,
  comparison: ReviewComparisonMode,
  file: StationPath.pipe(Schema.optional),
  hunk: Schema.String.pipe(Schema.optional),
  findingId: domainField(FindingId, value => developmentIdentifier("finding", value)).pipe(Schema.optionalKey),
  checkId: domainField(CheckRunId, value => developmentIdentifier("check-run", value)).pipe(Schema.optionalKey),
  runId: domainField(RunId, value => developmentIdentifier("run", value)).pipe(Schema.optionalKey),
  evidenceId: domainField(EvidenceId, value => developmentIdentifier("evidence", value)).pipe(Schema.optionalKey),
  conflictId: Schema.String.pipe(Schema.optional),
  reviewId: domainField(ReviewRunId, value => developmentIdentifier("review-run", value)).pipe(Schema.optionalKey),
  approvalId: domainField(ApprovalId, value => developmentIdentifier("approval", value)).pipe(Schema.optionalKey),
  principalId: domainField(PrincipalId, value => developmentIdentifier("principal", value)).pipe(Schema.optionalKey),
  conditionKey: Schema.String.pipe(Schema.optional),
  action: ReviewDeepLinkAction.pipe(Schema.optional),
}))();

export const ReviewDeepLinkParseResult = /* @__PURE__ */ (() => Schema.Union([Schema.Struct({
  status: Schema.Literal("not-review"),
}), Schema.Struct({
  status: Schema.Literal("valid"),
  state: Schema.suspend(() => ReviewDeepLinkState),
}), Schema.Struct({
  status: Schema.Literal("invalid"),
  reason: Schema.String,
})]))();
export type ReviewDeepLinkParseResult = typeof ReviewDeepLinkParseResult.Type;

export const ReviewLocationLike = /* @__PURE__ */ (() => Schema.Struct({
  pathname: Schema.String,
  search: Schema.String,
  hash: Schema.String,
}))();
export type ReviewLocationLike = typeof ReviewLocationLike.Type;

export interface ReviewHistoryLike {
  pushState(data: unknown, unused: string, url?: string | URL | null): void;
  replaceState(data: unknown, unused: string, url?: string | URL | null): void;
}

export interface ReviewWindowLike {
  readonly location: ReviewLocationLike;
  readonly history: ReviewHistoryLike;
  addEventListener(type: "popstate", listener: () => void): void;
  removeEventListener(type: "popstate", listener: () => void): void;
}

const TOKEN_RE = /^[A-Za-z0-9][A-Za-z0-9._:@+-]{0,255}$/;
const CONDITION_RE = /^[a-z0-9][a-z0-9._:-]{0,255}$/;
const MAX_SEARCH_LENGTH = 8_192;

const REVIEW_PARAMETERS = [
  "tab",
  "change",
  "candidate",
  "workspace",
  "section",
  "compare",
  "file",
  "hunk",
  "finding",
  "check",
  "run",
  "evidence",
  "conflict",
  "review",
  "approval",
  "principal",
  "condition",
  "action"
] as const;

// Pure parser construction leaves the exported Schema immutable and lets
// server bundles discard review-navigation code that only the editor consumes.
const parse = <S extends Schema.ConstraintDecoder<unknown>>(schema: S, onError: (error: Schema.SchemaError) => unknown) => {
  const decode = Schema.decodeUnknownResult(schema);
  return (value: unknown): S["Type"] => Result.getOrThrowWith(decode(value), onError);
};

const formatIssue = /* @__PURE__ */ (() => SchemaIssue.makeFormatterStandardSchemaV1())();
const decodeError = (error: Schema.SchemaError): unknown =>
  domainFailure(error.issue) ?? new TypeError(formatIssue(error.issue).issues[0]?.message ?? error.message);
const BoundedSearch = /* @__PURE__ */ (() => {
  const encoder = new TextEncoder();
  return Schema.String.check(Schema.makeFilter(value => encoder.encode(value).byteLength <= MAX_SEARCH_LENGTH));
})();
const boundedSearch = /* @__PURE__ */ (() => parse(BoundedSearch,
  () => new TypeError("Review deep-link query is invalid or too large.")))();

const single = (search: URLSearchParams, name: (typeof REVIEW_PARAMETERS)[number]): string | undefined => {
  const values = parse(Schema.Array(Schema.String).check(Schema.isMaxLength(1)),
    () => new TypeError(`Review deep link repeats ${name}.`))(search.getAll(name));
  return parse(Schema.UndefinedOr(Schema.String.check(Schema.isMinLength(1))),
    () => new TypeError(`Review deep link ${name} is empty.`))(values[0]);
};
const optionalText = (name: string) => Schema.optionalKey(
  Schema.String.annotate({ message: `Review deep-link ${name} must be a string.` })
);
const token = (label: string, field = label) => Schema.optionalKey(Schema.String.annotate({
  message: `Review deep-link ${field} must be a string.`
}).check(
  Schema.isPattern(TOKEN_RE, { message: `Review deep-link ${label} is invalid.` })
));
const sectionContract = /* @__PURE__ */ (() => parse(ReviewSection,
  () => new TypeError("Review deep-link section is unsupported.")))();
const comparisonContract = /* @__PURE__ */ (() => parse(ReviewComparisonMode,
  () => new TypeError("Review deep-link comparison is unsupported.")))();

const ReviewDeepLinkInput = /* @__PURE__ */ (() => {
  const ACTION_SECTIONS: Readonly<Record<ReviewDeepLinkAction, ReviewSection>> =
    Object.freeze({
      "resolve-conflict": "conflicts",
      "run-check": "checks",
      "complete-review": "reviews",
      "disposition-finding": "findings",
      "request-approval": "summary",
      "refresh-target": "summary",
      "refresh-authority": "summary"
    });
  return Schema.Struct({
    version: Schema.Literal(REVIEW_DEEP_LINK_VERSION).annotate({ message: "Review deep-link state has an unsupported version." })
      .annotateKey({ messageMissingKey: "Review deep-link state has an unsupported version." }),
    section: Schema.String.annotate({ message: "Review deep-link section must be a string." })
      .pipe(Schema.decodeTo(ReviewSection.annotate({ message: "Review deep-link section is unsupported." })))
      .annotateKey({ messageMissingKey: "Review deep-link section is unsupported." }),
    comparison: Schema.String.annotate({ message: "Review deep-link comparison must be a string." })
      .pipe(Schema.decodeTo(ReviewComparisonMode.annotate({ message: "Review deep-link comparison is unsupported." })))
      .annotateKey({ messageMissingKey: "Review deep-link comparison is unsupported." }),
    changeId: optionalText("changeId"), candidateId: optionalText("candidateId"),
    workspaceId: optionalText("workspaceId"),
    file: Schema.optionalKey(Schema.String.annotate({ message: "Review deep-link file must be a string." })
      .pipe(Schema.decodeTo(domainField(StationPath, stationPath)))),
    hunk: token("hunk"), findingId: optionalText("findingId"), checkId: optionalText("checkId"),
    runId: optionalText("runId"), evidenceId: optionalText("evidenceId"), conflictId: token("conflict", "conflictId"),
    reviewId: optionalText("reviewId"), approvalId: optionalText("approvalId"), principalId: optionalText("principalId"),
    conditionKey: Schema.optionalKey(Schema.String.annotate({ message: "Review deep-link conditionKey must be a string." }).check(Schema.isPattern(CONDITION_RE, { message: "Review deep-link condition is invalid." }))),
    action: Schema.optionalKey(Schema.String.annotate({ message: "Review deep-link action must be a string." })
      .pipe(Schema.decodeTo(ReviewDeepLinkAction.annotate({ message: "Review deep-link action is unsupported." }))))
  }).check(
    Schema.makeFilter(value => value.hunk === undefined || value.file !== undefined,
      { message: "Review deep-link hunk requires a file." }, true),
    Schema.makeFilter(value => value.file === undefined || value.workspaceId !== undefined,
      { message: "Review deep-link file requires a workspace." }, true),
    Schema.makeFilter(value => value.candidateId !== undefined || [value.file, value.workspaceId, value.hunk,
    value.findingId, value.checkId, value.runId, value.evidenceId, value.conflictId, value.reviewId,
    value.approvalId, value.principalId, value.conditionKey, value.action].every(item => item === undefined),
      { message: "Review deep-link selected identities require a candidate." }, true),
    Schema.makeFilter(value => value.action === undefined || value.conditionKey !== undefined,
      { message: "Review deep-link action requires a gate condition." }, true),
    Schema.makeFilter(value => value.action === undefined || ACTION_SECTIONS[value.action] === value.section,
      { message: "Review deep-link action does not match its section." }, true)
  );
})();

const ReviewDeepLinkHeader = /* @__PURE__ */ (() => Schema.StructWithRest(Schema.Struct({
  version: ReviewDeepLinkInput.fields.version,
  section: optionalText("section"), comparison: optionalText("comparison")
}), [Schema.Record(Schema.String, Schema.Unknown)]))();

// Snapshot descriptors before decoding any field. Unknown keys and accessors
// never enter the decoder, and the normalized result drops explicit undefined.
export const ReviewDeepLinkState = /* @__PURE__ */ (() => Schema.ObjectKeyword.annotate({
  message: "Review deep-link state must be an object."
}).check(Schema.makeFilter(value => typeof value === "object" && !Array.isArray(value), {
  message: "Review deep-link state must be an object."
})).pipe(
  Schema.decodeTo(Schema.Record(Schema.String, Schema.Unknown), {
    decode: SchemaGetter.transformOrFail((input: object) => Effect.try({
      try: () => {
        const prototype = Object.getPrototypeOf(input);
        if(prototype !== Object.prototype && prototype !== null) {
          throw new SchemaIssue.InvalidValue({ message: "Review deep-link state must be a plain object." });
        }
        const snapshot: Record<string, unknown> = Object.create(null);
        for(const key of Reflect.ownKeys(input)) {
          if(typeof key !== "string" || !Object.hasOwn(ReviewDeepLinkValue.fields, key)) {
            throw new SchemaIssue.InvalidValue({ message: "Review deep-link state contains an unknown field." });
          }
          const descriptor = Object.getOwnPropertyDescriptor(input, key);
          if(!descriptor || !("value" in descriptor) || !descriptor.enumerable) {
            throw new SchemaIssue.InvalidValue({ message: "Review deep-link state fields must be enumerable data properties." });
          }
          if(descriptor.value !== undefined) snapshot[key] = descriptor.value;
        }
        return snapshot;
      },
      catch: cause => SchemaIssue.isIssue(cause) ? cause : new SchemaIssue.InvalidValue({
        message: "Review deep-link state must be a readable plain object."
      })
    })),
    encode: SchemaGetter.transform((input: Record<string, unknown>) => input)
  }),
  Schema.decodeTo(ReviewDeepLinkHeader),
  Schema.decodeTo(ReviewDeepLinkInput),
  Schema.decodeTo(ReviewDeepLinkValue)
))();
export type ReviewDeepLinkState = typeof ReviewDeepLinkState.Type;
const stateContract = /* @__PURE__ */ (() => parse(ReviewDeepLinkState, decodeError))();

/** Snapshot and validate caller-owned state before serializing or navigating. */
const normalizeReviewDeepLinkState = (value: unknown): ReviewDeepLinkState =>
  Object.freeze(stateContract(value));

/** Parse a bounded query without throwing into the editor render path. */
export function parseReviewDeepLink(
  searchValue: string
): ReviewDeepLinkParseResult {
  return Result.match(Result.try(() => {
    searchValue = Result.getOrThrowWith(Schema.decodeUnknownResult(BoundedSearch)(searchValue),
      () => new TypeError("Review deep-link query is invalid or too large."));
    const search = new URLSearchParams(
      searchValue.startsWith("?") ? searchValue.slice(1) : searchValue
    );
    if (single(search, "tab") !== "review") {
      return Object.freeze({ status: "not-review" });
    }
    const sectionValue = sectionContract(single(search, "section") ?? "summary");
    const comparisonValue = comparisonContract(single(search, "compare") ?? "full-change");
    const change = single(search, "change");
    const candidate = single(search, "candidate");
    const workspace = single(search, "workspace");
    const fileValue = single(search, "file");
    const hunk = single(search, "hunk");
    const finding = single(search, "finding");
    const check = single(search, "check");
    const run = single(search, "run");
    const evidence = single(search, "evidence");
    const conflict = single(search, "conflict");
    const review = single(search, "review");
    const approval = single(search, "approval");
    const principal = single(search, "principal");
    const conditionKey = single(search, "condition");
    const action = single(search, "action");
    const state = normalizeReviewDeepLinkState({
      version: REVIEW_DEEP_LINK_VERSION,
      ...(change === undefined ? {} : { changeId: change }),
      ...(candidate === undefined ? {} : { candidateId: candidate }),
      ...(workspace === undefined ? {} : { workspaceId: workspace }),
      section: sectionValue,
      comparison: comparisonValue,
      ...(fileValue === undefined ? {} : { file: fileValue }),
      ...(hunk === undefined ? {} : { hunk }),
      ...(finding === undefined ? {} : { findingId: finding }),
      ...(check === undefined ? {} : { checkId: check }),
      ...(run === undefined ? {} : { runId: run }),
      ...(evidence === undefined ? {} : { evidenceId: evidence }),
      ...(conflict === undefined ? {} : { conflictId: conflict }),
      ...(review === undefined ? {} : { reviewId: review }),
      ...(approval === undefined ? {} : { approvalId: approval }),
      ...(principal === undefined ? {} : { principalId: principal }),
      ...(conditionKey === undefined ? {} : { conditionKey }),
      ...(action === undefined ? {} : { action })
    });
    return Object.freeze({ status: "valid", state });
  }), {
    onSuccess: result => result,
    onFailure: error => Object.freeze({
      status: "invalid",
      reason: error instanceof Error ? error.message : "Review deep link is invalid."
    })
  });
}

/** Canonically update only review-owned query parameters. */
export function reviewDeepLinkSearch(
  state: ReviewDeepLinkState,
  currentSearch = ""
): string {
  const snapshot = normalizeReviewDeepLinkState(state);
  currentSearch = boundedSearch(currentSearch);
  const params = new URLSearchParams(
    currentSearch.startsWith("?") ? currentSearch.slice(1) : currentSearch
  );
  for (const parameter of REVIEW_PARAMETERS) params.delete(parameter);
  params.set("tab", "review");
  if (snapshot.changeId !== undefined) params.set("change", snapshot.changeId);
  if (snapshot.candidateId !== undefined) {
    params.set("candidate", snapshot.candidateId);
  }
  if (snapshot.workspaceId !== undefined) {
    params.set("workspace", snapshot.workspaceId);
  }
  params.set("section", snapshot.section);
  params.set("compare", snapshot.comparison);
  if (snapshot.file !== undefined) params.set("file", snapshot.file);
  if (snapshot.hunk !== undefined) params.set("hunk", snapshot.hunk);
  if (snapshot.findingId !== undefined) {
    params.set("finding", snapshot.findingId);
  }
  if (snapshot.checkId !== undefined) params.set("check", snapshot.checkId);
  if (snapshot.runId !== undefined) params.set("run", snapshot.runId);
  if (snapshot.evidenceId !== undefined) {
    params.set("evidence", snapshot.evidenceId);
  }
  if (snapshot.conflictId !== undefined) {
    params.set("conflict", snapshot.conflictId);
  }
  if (snapshot.reviewId !== undefined) params.set("review", snapshot.reviewId);
  if (snapshot.approvalId !== undefined) {
    params.set("approval", snapshot.approvalId);
  }
  if (snapshot.principalId !== undefined) {
    params.set("principal", snapshot.principalId);
  }
  if (snapshot.conditionKey !== undefined) {
    params.set("condition", snapshot.conditionKey);
  }
  if (snapshot.action !== undefined) params.set("action", snapshot.action);
  return boundedSearch(`?${params.toString()}`);
}

/** Leave review routing while retaining query state owned by the station shell. */
export function reviewDeepLinkExitSearch(currentSearch = ""): string {
  currentSearch = boundedSearch(currentSearch);
  const params = new URLSearchParams(
    currentSearch.startsWith("?") ? currentSearch.slice(1) : currentSearch
  );
  for (const parameter of REVIEW_PARAMETERS) params.delete(parameter);
  const value = params.toString();
  return value.length === 0 ? "" : boundedSearch(`?${value}`);
}

const startedContract = /* @__PURE__ */ (() => parse(Schema.Literal(true),
  () => new TypeError("Review history controller is not started.")))();
const navigationModeContract = /* @__PURE__ */ (() => parse(Schema.Literals(["push", "replace"]),
  () => new TypeError("Review history navigation mode is invalid.")))();
const navigationResultContract = /* @__PURE__ */ (() => parse(Schema.Struct({
  status: Schema.Literal("valid"), state: ReviewDeepLinkState
}), () => new TypeError("Review history navigation state is invalid.")))();

export class ReviewHistoryController {
  readonly #window: ReviewWindowLike;
  readonly #listener: (result: ReviewDeepLinkParseResult) => void;
  readonly #popstate: () => void;
  #started = false;

  constructor(
    windowLike: ReviewWindowLike,
    listener: (result: ReviewDeepLinkParseResult) => void
  ) {
    this.#window = windowLike;
    this.#listener = listener;
    this.#popstate = () => {
      if (!this.#started) return;
      this.#listener(parseReviewDeepLink(this.#window.location.search));
    };
  }

  start(): void {
    if (this.#started) return;
    this.#window.addEventListener("popstate", this.#popstate);
    this.#started = true;
    try {
      this.#popstate();
    } catch (error) {
      this.#started = false;
      this.#window.removeEventListener("popstate", this.#popstate);
      throw error;
    }
  }

  stop(): void {
    if (!this.#started) return;
    this.#window.removeEventListener("popstate", this.#popstate);
    this.#started = false;
  }

  navigate(state: ReviewDeepLinkState, mode: "push" | "replace" = "push"): void {
    startedContract(this.#started);
    navigationModeContract(mode);
    const location = this.#window.location;
    const pathname = location.pathname;
    const currentSearch = location.search;
    const hash = location.hash;
    const search = reviewDeepLinkSearch(state, currentSearch);
    const parsed = parseReviewDeepLink(search);
    navigationResultContract(parsed);
    const url = `${pathname}${search}${hash}`;
    const historyState = Object.freeze({
      reviewDeepLinkVersion: REVIEW_DEEP_LINK_VERSION,
      search
    });
    if (mode === "push") {
      const current = parseReviewDeepLink(currentSearch);
      if (
        current.status === "valid" &&
        reviewDeepLinkSearch(current.state, currentSearch) === search
      ) {
        if (currentSearch !== search) {
          this.#window.history.replaceState(historyState, "", url);
        }
        this.#listener(parsed);
        return;
      }
    }
    if (mode === "replace") {
      this.#window.history.replaceState(historyState, "", url);
    } else {
      this.#window.history.pushState(historyState, "", url);
    }
    this.#listener(parsed);
  }
}
